/**
 * Desktop Navigation Web Component
 *
 * Manages hover/click dropdown interactions with debounce,
 * keyboard navigation, and accessibility.
 *
 * Supports two interaction modes via `data-interaction` attribute:
 * - "hover" (default): dropdowns open on mouseenter with debounce
 * - "click": dropdowns open on click only; items with links navigate on text click
 */
class DesktopNavComponent extends HTMLElement {
  constructor() {
    super();
    this.openTimeout = null;
    this.closeTimeout = null;
    this.activeDropdown = null;
    this.OPEN_DELAY = 150;
    this.CLOSE_DELAY = 400;
  }

  get isClickMode() {
    return this.getAttribute('data-interaction') === 'click';
  }

  connectedCallback() {
    this.items = this.querySelectorAll('.desktop-nav-item');
    this.linkGroups = this.querySelectorAll('.desktop-nav-link-group');
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  bindEvents() {
    this._onKeyDown = this.handleKeyDown.bind(this);
    this._onClickOutside = this.handleClickOutside.bind(this);

    document.addEventListener('keydown', this._onKeyDown);
    document.addEventListener('click', this._onClickOutside);

    this.items.forEach((item) => {
      const toggle = item.querySelector('.desktop-nav-item__toggle[aria-expanded]');
      const dropdown = item.querySelector('.desktop-nav-dropdown');

      if (!toggle || !dropdown) return;

      if (!this.isClickMode) {
        // Hover mode: open/close on mouseenter/mouseleave
        item._onMouseEnter = () => this.scheduleOpen(toggle, dropdown);
        item._onMouseLeave = () => this.scheduleClose(toggle, dropdown);
        item.addEventListener('mouseenter', item._onMouseEnter);
        item.addEventListener('mouseleave', item._onMouseLeave);

        // Bridge: re-entering dropdown cancels close
        dropdown._onMouseEnter = () => {
          clearTimeout(this.closeTimeout);
        };
        dropdown._onMouseLeave = () => {
          this.scheduleClose(toggle, dropdown);
        };
        dropdown.addEventListener('mouseenter', dropdown._onMouseEnter);
        dropdown.addEventListener('mouseleave', dropdown._onMouseLeave);
      } else {
        // Click mode: trigger area opens dropdown for items without links
        const trigger = item.querySelector('.desktop-nav-item__trigger');
        if (trigger) {
          trigger._onClickTrigger = (e) => {
            // If click was on a link <a>, let it navigate
            if (e.target.closest('a')) return;
            e.preventDefault();
            this.handleToggleClick(e, toggle, dropdown);
          };
          trigger.addEventListener('click', trigger._onClickTrigger);
        }
      }

      // Click on chevron toggle (works in both modes)
      toggle._onClick = (e) => this.handleToggleClick(e, toggle, dropdown);
      toggle.addEventListener('click', toggle._onClick);
    });

    this.linkGroups.forEach((group) => {
      const toggle = group.querySelector('.desktop-nav-link-group__toggle[aria-expanded]');
      const dropdown = group.querySelector('.desktop-nav-link-dropdown');

      if (!toggle || !dropdown) return;

      if (!this.isClickMode) {
        group._onMouseEnter = () => this.scheduleOpen(toggle, dropdown);
        group._onMouseLeave = () => this.scheduleClose(toggle, dropdown);
        group.addEventListener('mouseenter', group._onMouseEnter);
        group.addEventListener('mouseleave', group._onMouseLeave);

        dropdown._onMouseEnter = () => {
          clearTimeout(this.closeTimeout);
        };
        dropdown._onMouseLeave = () => {
          this.scheduleClose(toggle, dropdown);
        };
        dropdown.addEventListener('mouseenter', dropdown._onMouseEnter);
        dropdown.addEventListener('mouseleave', dropdown._onMouseLeave);
      }

      toggle._onClick = (e) => this.handleToggleClick(e, toggle, dropdown);
      toggle.addEventListener('click', toggle._onClick);
    });
  }

  unbindEvents() {
    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('click', this._onClickOutside);

    this.items.forEach((item) => {
      const toggle = item.querySelector('.desktop-nav-item__toggle[aria-expanded]');
      const dropdown = item.querySelector('.desktop-nav-dropdown');

      if (item._onMouseEnter) {
        item.removeEventListener('mouseenter', item._onMouseEnter);
        item.removeEventListener('mouseleave', item._onMouseLeave);
      }
      if (dropdown && dropdown._onMouseEnter) {
        dropdown.removeEventListener('mouseenter', dropdown._onMouseEnter);
        dropdown.removeEventListener('mouseleave', dropdown._onMouseLeave);
      }
      if (toggle && toggle._onClick) {
        toggle.removeEventListener('click', toggle._onClick);
      }

      const trigger = item.querySelector('.desktop-nav-item__trigger');
      if (trigger && trigger._onClickTrigger) {
        trigger.removeEventListener('click', trigger._onClickTrigger);
      }
    });

    this.linkGroups.forEach((group) => {
      const toggle = group.querySelector('.desktop-nav-link-group__toggle[aria-expanded]');
      const dropdown = group.querySelector('.desktop-nav-link-dropdown');

      if (group._onMouseEnter) {
        group.removeEventListener('mouseenter', group._onMouseEnter);
        group.removeEventListener('mouseleave', group._onMouseLeave);
      }
      if (dropdown && dropdown._onMouseEnter) {
        dropdown.removeEventListener('mouseenter', dropdown._onMouseEnter);
        dropdown.removeEventListener('mouseleave', dropdown._onMouseLeave);
      }
      if (toggle && toggle._onClick) {
        toggle.removeEventListener('click', toggle._onClick);
      }
    });
  }

  scheduleOpen(toggle, dropdown) {
    clearTimeout(this.openTimeout);
    clearTimeout(this.closeTimeout);
    this.openTimeout = setTimeout(() => {
      this.openDropdown(toggle, dropdown);
    }, this.OPEN_DELAY);
  }

  scheduleClose(toggle, dropdown) {
    clearTimeout(this.openTimeout);
    this.closeTimeout = setTimeout(() => {
      this.closeDropdown(toggle, dropdown);
    }, this.CLOSE_DELAY);
  }

  #findToggleForDropdown(dropdown) {
    const container = dropdown.closest('.desktop-nav-item')
      || dropdown.closest('.desktop-nav-link-group');
    if (!container) return null;
    const selector = container.classList.contains('desktop-nav-link-group')
      ? '.desktop-nav-link-group__toggle[aria-expanded]'
      : '.desktop-nav-item__toggle[aria-expanded]';
    return container.querySelector(selector);
  }

  openDropdown(toggle, dropdown) {
    // Close any other open dropdown first
    if (this.activeDropdown && this.activeDropdown !== dropdown) {
      const activeToggle = this.#findToggleForDropdown(this.activeDropdown);
      if (activeToggle) {
        this.closeDropdown(activeToggle, this.activeDropdown);
      }
    }

    toggle.setAttribute('aria-expanded', 'true');
    dropdown.setAttribute('data-active', '');
    this.activeDropdown = dropdown;
  }

  closeDropdown(toggle, dropdown) {
    toggle.setAttribute('aria-expanded', 'false');
    dropdown.removeAttribute('data-active');

    if (this.activeDropdown === dropdown) {
      this.activeDropdown = null;
    }
  }

  closeAll() {
    clearTimeout(this.openTimeout);
    clearTimeout(this.closeTimeout);

    this.items.forEach((item) => {
      const toggle = item.querySelector('.desktop-nav-item__toggle[aria-expanded]');
      const dropdown = item.querySelector('.desktop-nav-dropdown');
      if (toggle && dropdown) {
        this.closeDropdown(toggle, dropdown);
      }
    });

    this.linkGroups.forEach((group) => {
      const toggle = group.querySelector('.desktop-nav-link-group__toggle[aria-expanded]');
      const dropdown = group.querySelector('.desktop-nav-link-dropdown');
      if (toggle && dropdown) {
        this.closeDropdown(toggle, dropdown);
      }
    });
  }

  handleToggleClick(e, toggle, dropdown) {
    e.preventDefault();
    e.stopPropagation();
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      this.closeDropdown(toggle, dropdown);
    } else {
      this.openDropdown(toggle, dropdown);
    }
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' && this.activeDropdown) {
      const activeToggle = this.#findToggleForDropdown(this.activeDropdown);

      this.closeAll();

      if (activeToggle) {
        activeToggle.focus();
      }
    }
  }

  handleClickOutside(e) {
    if (!this.contains(e.target) && this.activeDropdown) {
      this.closeAll();
    }
  }
}

if (!customElements.get('desktop-nav-component')) {
  customElements.define('desktop-nav-component', DesktopNavComponent);
}
